/**
 * QR Scanner component using device camera.
 * Uses BarcodeDetector API (native) with fallback message.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { IconCamera, IconX } from "../../../../components/Icons.tsx";
import { haptic } from "../../../../lib/telegram.ts";
import { adminLog } from "../../../../lib/logger.ts";

// Check if BarcodeDetector is available
const hasBarcodeDetector = typeof BarcodeDetector !== "undefined";

export interface QRScannerProps {
  /** Callback when QR code is scanned */
  onScan: (data: string) => void;
  /** Whether scanner is active */
  isActive: boolean;
  /** Callback to close scanner */
  onClose: () => void;
}

export function QRScanner({ onScan, isActive, onClose }: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number | null>(null);
  const detectorRef = useRef<BarcodeDetector | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Scan frame using BarcodeDetector
  const scanFrame = useCallback(async () => {
    if (!videoRef.current || !detectorRef.current) {
      animationRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    const video = videoRef.current;

    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    try {
      const barcodes = await detectorRef.current.detect(video);
      
      if (barcodes.length > 0) {
        const qrCode = barcodes.find(b => b.format === "qr_code");
        if (qrCode && qrCode.rawValue) {
          haptic("success");
          onScan(qrCode.rawValue);
          return; // Stop scanning after successful read
        }
      }
    } catch {
      // Detection failed, continue
    }

    animationRef.current = requestAnimationFrame(scanFrame);
  }, [onScan]);

  // Start camera
  const startCamera = useCallback(async () => {
    setIsInitializing(true);
    setError(null);

    // Check for BarcodeDetector support
    if (!hasBarcodeDetector) {
      setError("QR-сканер не поддерживается в этом браузере. Используйте ручной ввод номера билета.");
      setIsInitializing(false);
      return;
    }

    try {
      // Initialize BarcodeDetector
      detectorRef.current = new BarcodeDetector({ formats: ["qr_code"] });

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsInitializing(false);
        scanFrame();
      }
    } catch (err) {
      adminLog.error("Camera error:", err);
      setError("Не удалось получить доступ к камере");
      setIsInitializing(false);
    }
  }, [scanFrame]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    detectorRef.current = null;
  }, []);

  // Handle active state
  useEffect(() => {
    if (isActive) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isActive, startCamera, stopCamera]);

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center"
        aria-label="Закрыть"
      >
        <IconX className="w-6 h-6" />
      </button>

      {/* Video feed */}
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        playsInline
        muted
      />


      {/* Scanning overlay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {/* Scanning frame */}
        <div className="relative w-64 h-64">
          {/* Corner markers */}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
          <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />

          {/* Scanning line animation */}
          <div className="absolute inset-x-4 top-4 h-0.5 bg-teal-400 animate-pulse" />
        </div>
      </div>

      {/* Status text */}
      <div className="absolute bottom-20 inset-x-0 text-center">
        {isInitializing ? (
          <div className="flex items-center justify-center gap-2 text-white">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>Инициализация камеры...</span>
          </div>
        ) : error ? (
          <div className="text-red-400 px-4">
            <IconCamera className="w-8 h-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        ) : (
          <p className="text-white/80">Наведите камеру на QR-код билета</p>
        )}
      </div>
    </div>
  );
}
